import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Lock, PlugZap, ShieldOff } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { apiGet, apiPost } from '../api/client';
import { ByoDbGuide, ByoDbGuideToggle, FAILURE_BN } from './ByoDbGuide';

/**
 * The connection details for a customer's own MySQL server (the "self-hosted" database
 * option), plus the optional "Test connection" action.
 *
 * ONE component, used by all three create surfaces — admin/reseller create, the signup
 * wizard, and an owner adding another business. They previously could not drift because only
 * one of them had these fields at all; now that all three do, a single component is what
 * keeps "what the form accepts" identical everywhere. The server-side validator
 * (validateSelfHostedCreds) is the matching single source on the other side.
 */

export interface ByoDbValue {
  host: string;
  /** Kept as a string because it comes from a text input; coerced at the call site. */
  port: string;
  dbName: string;
  username: string;
  password: string;
}

export const EMPTY_BYO_DB: ByoDbValue = { host: '', port: '3306', dbName: '', username: '', password: '' };

/**
 * Client-side shape check, shared by every wizard's "can I submit" logic.
 *
 * Deliberately mirrors the server's rules rather than being laxer: a form that accepts a
 * value the server then rejects makes the customer guess which field was wrong, and the
 * server's message arrives after they have already committed to the step.
 */
export function byoDbError(v: ByoDbValue): string | null {
  if (!v.host.trim()) return 'Enter the address of your MySQL server.';
  if (!v.dbName.trim()) return 'Enter the name of the database to use.';
  if (!v.username.trim()) return 'Enter the database username.';
  if (!v.password) return 'Enter the database password.';
  const port = Number(v.port);
  if (!Number.isInteger(port) || port <= 0 || port > 65535) return 'Port must be a number between 1 and 65535.';
  if (!/^[A-Za-z0-9_][A-Za-z0-9_-]{0,63}$/.test(v.dbName.trim())) {
    return 'Database name must be letters, numbers, underscore or dash, and cannot start with a dash.';
  }
  if (!/^[A-Za-z0-9_][A-Za-z0-9_-]{0,63}$/.test(v.username.trim())) {
    return 'Database username must be letters, numbers, underscore or dash, and cannot start with a dash.';
  }
  return null;
}

/** The payload shape POST /businesses and POST /auth/signup both expect. */
export function toByoDbCreds(v: ByoDbValue) {
  return {
    host: v.host.trim(),
    port: Number(v.port) || 3306,
    dbName: v.dbName.trim(),
    username: v.username.trim(),
    password: v.password,
  };
}

type TestResult = {
  ok: boolean;
  message: string;
  encrypted?: boolean;
  /** Stable slug for the failure, so the panel can show its own Bangla copy. */
  reason?: keyof typeof FAILURE_BN;
  /** English cause + fix from the server. Shown as-is when we have no Bangla for the slug. */
  cause?: string;
  fix?: string;
  /** The driver's own words. Useful to whoever is debugging; never the whole story. */
  raw?: string;
};

export function ByoDbFields({
  value,
  onChange,
  /**
   * Show the "Test connection" button. OFF on public signup by design: the endpoint behind it
   * opens a connection to any host you name and tells you what happened, which is a network
   * prober if it sits in front of a login. Signup performs the same check inside its own
   * verified submit instead, so nothing is lost but the button.
   */
  canTest = false,
}: {
  value: ByoDbValue;
  onChange: (v: ByoDbValue) => void;
  canTest?: boolean;
}) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  // The address a customer must whitelist comes from the API, not a constant in this bundle:
  // it is the machine that actually opens the connection, and a stale copy here would send
  // people to open their firewall to the wrong IP. Falls back to hiding the address rather
  // than guessing one.
  const [sourceIp, setSourceIp] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    apiGet<{ sourceIp: string }>('/byo-db-requirements')
      .then((r) => { if (live) setSourceIp(r.sourceIp); })
      .catch(() => { /* the guide still explains the steps; only the address is missing */ });
    return () => { live = false; };
  }, []);
  const set = (patch: Partial<ByoDbValue>) => {
    setResult(null); // any edit invalidates the previous verdict
    onChange({ ...value, ...patch });
  };

  async function test() {
    const invalid = byoDbError(value);
    if (invalid) {
      setResult({ ok: false, message: invalid });
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const r = await apiPost<TestResult>('/businesses/db-connection-test', toByoDbCreds(value));
      setResult(r);
    } catch (e) {
      setResult({ ok: false, message: e instanceof Error ? e.message : 'We could not reach that database.' });
    } finally {
      setTesting(false);
    }
  }

  const bn = result && !result.ok && result.reason ? FAILURE_BN[result.reason] : undefined;

  return (
    <div className="mt-3 rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 space-y-3">
      {/* The guide sits ABOVE the fields: the firewall and grant work has to happen on the
          customer's server BEFORE these values can possibly work, so offering it after the
          inputs would be offering it after the failure. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-zinc-600 dark:text-zinc-300">
          Your server must accept connections from us.
          <span lang="bn" className="block">আমাদের সার্ভার থেকে সংযোগ নেওয়ার অনুমতি আপনার সার্ভারে থাকতে হবে।</span>
        </p>
        <ByoDbGuideToggle open={showGuide} onToggle={() => setShowGuide((v) => !v)} />
      </div>
      {showGuide && <ByoDbGuide sourceIp={sourceIp ?? 'our server'} onClose={() => setShowGuide(false)} />}

      <div className="grid sm:grid-cols-2 gap-3">
        <Input
          label="Database host"
          hint="The public address of your MySQL server."
          value={value.host}
          onChange={(e) => set({ host: e.target.value })}
          required
        />
        <Input label="Port" value={value.port} onChange={(e) => set({ port: e.target.value })} inputMode="numeric" />
        <Input
          label="Database name"
          hint="A new, empty database — we never write over an existing one."
          value={value.dbName}
          onChange={(e) => set({ dbName: e.target.value })}
          required
        />
        <Input label="Username" value={value.username} onChange={(e) => set({ username: e.target.value })} required />
        <Input
          label="Password"
          type="password"
          value={value.password}
          onChange={(e) => set({ password: e.target.value })}
          required
          className="sm:col-span-2"
        />
      </div>

      {canTest && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={test}
            loading={testing}
            iconLeft={<PlugZap className="w-4 h-4" />}
            className="min-h-[44px]"
          >
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            Checks we can reach it and that it is empty.
          </span>
        </div>
      )}

      {result && (
        <div
          role="status"
          className={
            'flex items-start gap-2 rounded-md border p-3 text-xs ' +
            (result.ok
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200')
          }
        >
          {result.ok ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-px" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-px" />
          )}
          <div className="min-w-0">
            {/* A failure is shown as CAUSE then FIX, in both languages, because "connect
                ETIMEDOUT" is a true sentence that tells a shop owner nothing they can act on.
                The driver's own text is kept, but demoted — it is for whoever is debugging. */}
            {result.ok || !result.cause ? (
              <span>{result.message}</span>
            ) : (
              <>
                <span className="block">{result.cause}</span>
                {bn && <span lang="bn" className="block mt-0.5 opacity-90">{bn.cause}</span>}
                <span className="block mt-1.5 font-medium">{result.fix}</span>
                {bn && <span lang="bn" className="block mt-0.5 font-medium opacity-90">{bn.fix(sourceIp ?? 'our server')}</span>}
                <button
                  type="button"
                  onClick={() => setShowGuide(true)}
                  className="mt-2 inline-flex min-h-[36px] items-center underline underline-offset-2 font-medium
                             transition duration-100 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
                >
                  Open the setup guide · সেটআপ গাইড দেখুন
                </button>
                {result.raw && (
                  <span className="block mt-2 text-[10px] font-mono opacity-70 break-all">{result.raw}</span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/*
        The honest version of what used to be a flat "we connect over TLS" claim, which the
        code did not implement at all. We try an encrypted connection first and fall back to
        an unencrypted one if the server does not offer TLS — and record which happened, so
        the answer is a fact about each customer rather than a promise on a page.
      */}
      <p className="flex items-start gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
        {result?.ok && result.encrypted === false ? (
          <ShieldOff className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
        ) : (
          <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
        )}
        <span>
          We connect over an encrypted link when your server supports it, and fall back to an
          unencrypted one when it does not. Your password is encrypted at rest either way, and we
          set up the POS tables in the database you name here.
        </span>
      </p>
    </div>
  );
}
